import React, { useState, useEffect, useMemo } from "react";
import {
  PlusIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  Squares2X2Icon,
  Bars3Icon,
  UserPlusIcon,
  Cog6ToothIcon,
  PencilIcon,
  XMarkIcon,
  InformationCircleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import {
  ProjectFormElement,
  WorkflowContainer,
  WorkflowContainerStatus,
} from "../types";
import { apiService } from "../services/apiService";
import { ProjectFormBuilder } from "../components/forms/ProjectFormBuilder";
import { WorkflowMiniPreview } from "../components/workflow/WorkflowMiniPreview";
import { WorkflowStartModal } from "../components/workflow/WorkflowStartModal";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";
import { buildOrderedIds, sortByOrder } from "../utils/orderUtils";

const NAV_REFRESH_EVENT = "nav:refresh";

export const ProjectManagement: React.FC = () => {
  const { t, i18n } = useTranslation([
    "projects",
    "common",
    "workflows",
    "errors",
  ]);
  const {
    getWorkflowLabel,
    hasWorkflowTerminologyOverride,
    isAdmin,
    isManager,
  } = useAuth();
  const navigate = useNavigate();
  const canCreateProject = isAdmin() || isManager();

  // Company-specific terminology
  const workflowText = getWorkflowLabel();
  const resolvedLanguage = i18n.resolvedLanguage || i18n.language || "tr";
  const workflowTextLower = workflowText.toLocaleLowerCase(resolvedLanguage);
  const useWorkflowOverride = hasWorkflowTerminologyOverride();
  const workflowNameText = useWorkflowOverride
    ? t("workflows:customTerminology.name", { label: workflowText })
    : t("workflows:workflowName");
  const workflowDescText = useWorkflowOverride
    ? t("workflows:customTerminology.description", { label: workflowText })
    : t("workflows:workflowDescription");
  const createWorkflowText = useWorkflowOverride
    ? t("workflows:customTerminology.create", { label: workflowText })
    : t("workflows:createWorkflow");
  const editWorkflowText = useWorkflowOverride
    ? t("workflows:customTerminology.edit", { label: workflowText })
    : t("workflows:editWorkflow");
  const noWorkflowsText = useWorkflowOverride
    ? t("workflows:customTerminology.none", { label: workflowTextLower })
    : t("workflows:noWorkflows");
  const workflowCreatedText = useWorkflowOverride
    ? t("workflows:customTerminology.created", { label: workflowText })
    : t("workflows:workflowCreated");
  const projectWorkflowCreatedText = useWorkflowOverride
    ? t("workflows:customTerminology.created", { label: workflowText })
    : t("projects:workflowCreated");
  const noWorkflowsCommonText = useWorkflowOverride
    ? t("workflows:customTerminology.none", { label: workflowTextLower })
    : t("common:noWorkflows");
  const createFirstWorkflowText = useWorkflowOverride
    ? t("workflows:customTerminology.createFirst", { label: workflowTextLower })
    : t("common:createFirstWorkflow");

  const [projects, setProjects] = useState<WorkflowContainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [selectedProjectForDetails, setSelectedProjectForDetails] =
    useState<WorkflowContainer | null>(null);
  const [selectedWorkflowForDetails, setSelectedWorkflowForDetails] = useState<
    any | null
  >(null);
  const [editProjectManagerIds, setEditProjectManagerIds] = useState<number[]>(
    [],
  );
  const [editFormElements, setEditFormElements] = useState<
    ProjectFormElement[]
  >([]);
  const [projectWorkflows, setProjectWorkflows] = useState<Record<number, any>>(
    {},
  );
  const [hoveredProjectId, setHoveredProjectId] = useState<number | null>(null);
  const [showWorkflowStartModal, setShowWorkflowStartModal] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | null>(
    null,
  );
  const [selectedProjectIdForWorkflow, setSelectedProjectIdForWorkflow] =
    useState<number | null>(null);
  const [workflowOrder, setWorkflowOrder] = useState<number[]>([]);
  const [draggedProjectId, setDraggedProjectId] = useState<number | null>(null);
  const [dragOverProjectId, setDragOverProjectId] = useState<number | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Helper function to get form value from formElements
  const getProjectFormValue = (
    project: WorkflowContainer,
    fieldId: string,
  ): any => {
    if (!project?.formElements) return undefined;
    const element = project.formElements.find((el) => el.id === fieldId);
    return element?.value;
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const settings = await apiService.getUserSettings();
        if (Array.isArray(settings.workflowContainerOrder)) {
          setWorkflowOrder(settings.workflowContainerOrder);
        }
      } catch (error) {
        console.error("Error loading user settings:", error);
      }
    };

    loadUserSettings();
  }, []);

  useEffect(() => {
    if (workflowOrder.length === 0 || !projects || projects.length === 0) {
      return;
    }

    const projectIds = projects
      .map((project) => project?.id)
      .filter((id): id is number => typeof id === "number");
    if (projectIds.length === 0) {
      return;
    }

    const missingIds = projectIds.filter((id) => !workflowOrder.includes(id));
    if (missingIds.length === 0) {
      return;
    }

    const trimmedOrder = workflowOrder.filter((id) => projectIds.includes(id));
    const nextOrder = [...missingIds, ...trimmedOrder];

    setWorkflowOrder(nextOrder);
    persistWorkflowOrder(nextOrder);
  }, [projects, workflowOrder]);

  const persistWorkflowOrder = async (order: number[]) => {
    try {
      await apiService.updateUserSettings({ workflowContainerOrder: order });
      window.dispatchEvent(
        new CustomEvent("user:settings-updated", {
          detail: { workflowContainerOrder: order },
        }),
      );
    } catch (error) {
      console.error("Error saving workflow order:", error);
      toast.error(t("errors:updateFailed"));
    }
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      const [data, allWorkflows] = await Promise.all([
        apiService.getAllWorkflowContainers(),
        apiService.getWorkflows(),
      ]);

      setProjects(data || []);

      const workflowsByProject: Record<number, any> = {};
      for (const workflow of allWorkflows || []) {
        const workflowContainerId = workflow?.workflowContainerId;
        if (
          typeof workflowContainerId === "number" &&
          workflowsByProject[workflowContainerId] === undefined
        ) {
          workflowsByProject[workflowContainerId] = workflow;
        }
      }

      setProjectWorkflows(workflowsByProject);
    } catch (error) {
      console.error("Error loading projects:", error);
      setProjects([]);
      toast.error(t("projects:projectsLoadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (projectData: any) => {
    try {
      // Extract workflowName and projectManagerIds from projectData (passed by ProjectFormBuilder)
      const { workflowName, projectManagerIds, ...projectFields } = projectData;

      // Create project with workflow name
      const payload = {
        ...projectFields,
        workflowName: workflowName || workflowNameText, // Fallback
      };

      const createdProject = await apiService.createWorkflowContainer(payload);

      if (Array.isArray(projectManagerIds) && projectManagerIds.length > 0) {
        try {
          const workflow = await apiService.getProjectWorkflow(
            createdProject.id,
          );
          if (workflow?.id) {
            await apiService.setWorkflowProjectManagers(
              workflow.id,
              projectManagerIds,
            );
          }
        } catch (error) {
          console.error("Error assigning project managers:", error);
        }
      }
      setShowCreateForm(false);
      toast.success(projectWorkflowCreatedText);

      // Reload projects and workflows to get updated data
      await loadProjects();
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(NAV_REFRESH_EVENT, {
            detail: { workflows: true, projectInstances: true },
          }),
        );
      }
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error(t("errors:createFailed"));
    }
  };

  const getStatusColor = (status: WorkflowContainerStatus) => {
    switch (status) {
      case WorkflowContainerStatus.PLANNING:
        return "bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-200";
      case WorkflowContainerStatus.IN_PROGRESS:
        return "bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 border border-blue-200";
      case WorkflowContainerStatus.COMPLETED:
        return "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200";
      case WorkflowContainerStatus.ON_HOLD:
        return "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-800 border border-slate-200";
      case WorkflowContainerStatus.CANCELLED:
        return "bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200";
      default:
        return "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-800 border border-slate-200";
    }
  };

  const getStatusDisplayName = (status: WorkflowContainerStatus): string => {
    switch (status) {
      case WorkflowContainerStatus.PLANNING:
        return t("projects:status.planning");
      case WorkflowContainerStatus.IN_PROGRESS:
        return t("dashboard:inProgress");
      case WorkflowContainerStatus.COMPLETED:
        return t("dashboard:completed");
      case WorkflowContainerStatus.ON_HOLD:
        return t("projects:status.onHold");
      case WorkflowContainerStatus.CANCELLED:
        return t("dashboard:cancelled");
      default:
        return status;
    }
  };

  const handleEditWorkflow = async (projectId: number) => {
    try {
      // Projeye ait workflow'u bul
      const workflow = await apiService.getProjectWorkflow(projectId);
      if (workflow) {
        // Workflow düzenleme sayfasına git
        navigate(`/workflows/${workflow.id}/edit`);
      } else {
        // Workflow bulunamazsa yeni oluştur ve düzenlemeye git
        toast.error(noWorkflowsText);
      }
    } catch (error) {
      console.error("Error opening workflow:", error);
      toast.error(t("errors:loadFailed"));
    }
  };

  const handleViewWorkflowDetails = (workflow: WorkflowContainer) => {
    navigate(`/project-workflows/${workflow.id}`);
  };
  const handleAddMember = (project: WorkflowContainer) => {
    navigate(`/project-workflows/${project.id}/add-member`);
  };

  const handleEditDetails = async (project: WorkflowContainer) => {
    try {
      const workflow = projectWorkflows[project.id] || null;
      setSelectedWorkflowForDetails(workflow);

      if (workflow?.id && (isAdmin() || isManager())) {
        try {
          const ids = await apiService.getWorkflowProjectManagers(workflow.id);
          setEditProjectManagerIds(Array.isArray(ids) ? ids : []);
        } catch (error) {
          console.error("Error loading project managers:", error);
          setEditProjectManagerIds([]);
        }
      } else {
        setEditProjectManagerIds([]);
      }

      let currentProject = project;
      if (!project.formElements) {
        currentProject = await apiService.getWorkflowContainerById(project.id);
      }

      setSelectedProjectForDetails(currentProject);
      setEditFormElements(currentProject.formElements || []);
      setShowEditDetailsModal(true);
    } catch (error) {
      console.error("Error loading project details:", error);
      toast.error(t("errors:loadFailed"));
    }
  };

  const handleSaveDetails = async (formData: any) => {
    try {
      if (!selectedProjectForDetails) return;

      const workflow = selectedWorkflowForDetails;
      if (
        workflow &&
        formData.workflowName &&
        formData.workflowName !== workflow.name
      ) {
        await apiService.updateWorkflow(workflow.id, {
          name: formData.workflowName,
          workflowContainerId: workflow.workflowContainerId,
        });
        setProjectWorkflows((prev) => ({
          ...prev,
          [selectedProjectForDetails.id]: {
            ...prev[selectedProjectForDetails.id],
            name: formData.workflowName,
          },
        }));
      }

      const updatedProject = {
        ...selectedProjectForDetails,
        formElements: formData.elements,
      };

      await apiService.updateProject(
        selectedProjectForDetails.id,
        updatedProject,
      );

      if (
        (isAdmin() || isManager()) &&
        workflow?.id &&
        Array.isArray(formData.projectManagerIds)
      ) {
        try {
          await apiService.setWorkflowProjectManagers(
            workflow.id,
            formData.projectManagerIds,
          );
          setEditProjectManagerIds(formData.projectManagerIds);
        } catch (error) {
          console.error("Error assigning project managers:", error);
        }
      }

      setProjects((prev) =>
        prev.map((project) =>
          project.id === selectedProjectForDetails.id
            ? { ...project, formElements: formData.elements }
            : project,
        ),
      );

      setEditFormElements(formData.elements);
      setShowEditDetailsModal(false);
      setSelectedProjectForDetails(null);
      setSelectedWorkflowForDetails(null);
      toast.success(t("workflows:detailsUpdated"));
    } catch (error) {
      console.error("Error updating workflow details:", error);
      toast.error(t("errors:updateFailed"));
    }
  };

  const handleStartWorkflow = async (projectId: number) => {
    const workflow = projectWorkflows[projectId];

    if (!workflow) {
      toast.error(noWorkflowsText);
      return;
    }

    // Open the workflow start modal
    setSelectedProjectIdForWorkflow(projectId);
    setSelectedWorkflowId(workflow.id);
    setShowWorkflowStartModal(true);
  };

  const handleWorkflowStartSuccess = async () => {
    toast.success(workflowCreatedText);
    await loadProjects(); // Refresh to update workflow status
  };

  // Filter projects based on search
  const orderedProjects = useMemo(() => {
    if (!projects || projects.length === 0) return [];
    return sortByOrder(projects, workflowOrder, (project) => project.id);
  }, [projects, workflowOrder]);

  const filteredProjects = useMemo(() => {
    if (!orderedProjects || orderedProjects.length === 0) return [];

    return orderedProjects.filter((project) => {
      if (!project || !project.id) return false;

      // Apply search filter
      if (searchTerm) {
        const workflowName =
          projectWorkflows[project.id]?.name ||
          getProjectFormValue(project, "name") ||
          "";
        const projectName = getProjectFormValue(project, "name") || "";
        const description =
          projectWorkflows[project.id]?.description ||
          getProjectFormValue(project, "description") ||
          "";

        return (
          workflowName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          description.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      return true;
    });
  }, [orderedProjects, projectWorkflows, searchTerm]);

  const handleDragStart = (projectId: number) => {
    setDraggedProjectId(projectId);
    setDragOverProjectId(null);
    setHoveredProjectId(null);
  };

  const handleDragOver = (
    event: React.DragEvent<HTMLDivElement>,
    projectId: number,
  ) => {
    event.preventDefault();
    if (draggedProjectId !== projectId) {
      setDragOverProjectId(projectId);
    }
  };

  const handleDragLeave = () => {
    setDragOverProjectId(null);
  };

  const handleDragEnd = () => {
    setDragOverProjectId(null);
    setDraggedProjectId(null);
  };

  const handleDrop = async (
    event: React.DragEvent<HTMLDivElement>,
    targetProjectId: number,
  ) => {
    event.preventDefault();
    setDragOverProjectId(null);

    if (draggedProjectId === null || draggedProjectId === targetProjectId) {
      setDraggedProjectId(null);
      return;
    }

    const currentOrder = buildOrderedIds(
      orderedProjects,
      workflowOrder,
      (project) => project.id,
    );
    const fromIndex = currentOrder.indexOf(draggedProjectId);
    const toIndex = currentOrder.indexOf(targetProjectId);

    if (fromIndex === -1 || toIndex === -1) {
      setDraggedProjectId(null);
      return;
    }

    const nextOrder = [...currentOrder];
    nextOrder.splice(fromIndex, 1);
    nextOrder.splice(toIndex, 0, draggedProjectId);

    setWorkflowOrder(nextOrder);
    setDraggedProjectId(null);
    await persistWorkflowOrder(nextOrder);
  };

  // Pagination logic
  const totalPages =
    itemsPerPage === -1 ? 1 : Math.ceil(filteredProjects.length / itemsPerPage);
  const paginatedProjects = useMemo(() => {
    if (itemsPerPage === -1) {
      return filteredProjects;
    }
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredProjects.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredProjects, currentPage, itemsPerPage]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (loading) {
    return (
      <div className="app-page pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-slate-200 border-t-blue-600 mx-auto mb-4"></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-blue-400 animate-pulse"></div>
          </div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            {t("projects:loadingProjects")}
          </h3>
          <p className="text-slate-500">{t("common:loading")}</p>
        </div>
      </div>
    );
  }

  if (showCreateForm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[95vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <span className="text-2xl">🚀</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {createWorkflowText}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {workflowDescText}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateForm(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>
          </div>

          {/* Form Builder Content */}
          <div className="flex-1 overflow-hidden">
            <ProjectFormBuilder
              onSubmit={handleCreateProject}
              onCancel={() => setShowCreateForm(false)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page pt-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="mb-4 flex justify-stretch sm:justify-end">
          {canCreateProject && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex w-full items-center justify-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 sm:w-auto"
            >
              <PlusIcon className="w-5 h-5" />
              <span className="font-semibold">{createWorkflowText}</span>
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="mb-5">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("projects:searchProjectTypes")}
              className="w-full pl-11 pr-10 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white shadow-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Projects List - Compact Horizontal View */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20">
          {/* Table Header - Hidden on Mobile */}
          <div className="hidden lg:block bg-gradient-to-r from-slate-50 to-blue-50/50 border-b border-slate-200 px-5 py-2.5">
            <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-slate-600 uppercase tracking-wide">
              <div className="col-span-3">{workflowNameText}</div>
              <div className="col-span-2">{t("common:status")}</div>
              <div className="col-span-1">{t("common:nodes")}</div>
              <div className="col-span-2">{t("common:edges")}</div>
              <div className="col-span-4 text-right">{t("common:actions")}</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-slate-100">
            {paginatedProjects.length > 0 &&
              paginatedProjects.map((project, index) => {
                const isNearBottom = index >= paginatedProjects.length - 3;
                return (
                  <div
                    key={project.id}
                    className={`relative group transition-all duration-200 ${
                      draggedProjectId !== null
                        ? "hover:bg-transparent"
                        : "hover:bg-blue-50/30"
                    } ${
                      dragOverProjectId === project.id
                        ? "ring-2 ring-blue-300 bg-blue-50/30"
                        : ""
                    }`}
                    onMouseEnter={() => setHoveredProjectId(project.id)}
                    onMouseLeave={() => setHoveredProjectId(null)}
                    draggable={canCreateProject}
                    onDragStart={() => handleDragStart(project.id)}
                    onDragOver={(event) => handleDragOver(event, project.id)}
                    onDragLeave={handleDragLeave}
                    onDragEnd={handleDragEnd}
                    onDrop={(event) => handleDrop(event, project.id)}
                  >
                    {/* Project Row */}
                    <div className="px-4 lg:px-5 py-3">
                      {/* Desktop Layout (lg+) */}
                      <div className="hidden lg:grid lg:grid-cols-12 gap-4 items-center">
                        {/* Project Name */}
                        <div className="col-span-3">
                          <div className="min-w-0 flex items-center gap-2">
                            {canCreateProject && (
                              <span
                                className="text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing"
                                title="Sürükleyerek sırala"
                              >
                                <Bars3Icon className="w-4 h-4" />
                              </span>
                            )}
                            <h3 className="text-xs font-bold text-slate-800 truncate group-hover:text-blue-700 transition-colors">
                              {projectWorkflows[project?.id]?.name ||
                                getProjectFormValue(project, "name") ||
                                workflowNameText}
                            </h3>
                          </div>
                        </div>

                        {/* Status */}
                        <div className="col-span-2">
                          <span
                            className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(
                              project?.status,
                            )}`}
                          >
                            {getStatusDisplayName(project?.status)}
                          </span>
                        </div>

                        {/* Nodes */}
                        <div className="col-span-1">
                          <div className="flex items-center space-x-1.5">
                            <Squares2X2Icon className="w-4 h-4 text-blue-500" />
                            <span className="text-xs font-bold text-slate-800">
                              {projectWorkflows[project?.id]?.nodes?.length ||
                                0}
                            </span>
                            <span className="text-xs text-slate-500">
                              {t("workflows:addNode")}
                            </span>
                          </div>
                        </div>

                        {/* Edges */}
                        <div className="col-span-2">
                          <div className="flex items-center space-x-1.5">
                            <ChartBarIcon className="w-4 h-4 text-purple-500" />
                            <span className="text-xs font-bold text-slate-800">
                              {projectWorkflows[project?.id]?.edges?.length ||
                                0}
                            </span>
                            <span className="text-xs text-slate-500">
                              {t("workflows:connectNodes")}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="col-span-4">
                          <div className="flex items-center justify-end space-x-1">
                            {projectWorkflows[project?.id] && (
                              <>
                                <button
                                  onClick={() => handleAddMember(project)}
                                  className="flex items-center space-x-1 px-2.5 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-xs font-semibold whitespace-nowrap"
                                  title={t("projects:addMember")}
                                >
                                  <UserPlusIcon className="w-3.5 h-3.5" />
                                  <span>{t("projects:addMember")}</span>
                                </button>

                                <button
                                  onClick={() =>
                                    handleEditWorkflow(project?.id!)
                                  }
                                  className="flex items-center space-x-1 px-2.5 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-xs font-semibold whitespace-nowrap"
                                  title={editWorkflowText}
                                >
                                  <Cog6ToothIcon className="w-3.5 h-3.5" />
                                  <span>{t("workflows:design")}</span>
                                </button>

                                <button
                                  onClick={() =>
                                    handleStartWorkflow(project?.id!)
                                  }
                                  className="flex items-center space-x-1 px-2.5 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-xs font-semibold whitespace-nowrap"
                                  title={t("projects:createProject")}
                                >
                                  <RocketLaunchIcon className="w-3.5 h-3.5" />
                                  <span>{t("projects:createProject")}</span>
                                </button>

                                <button
                                  onClick={() => handleEditDetails(project)}
                                  className="flex items-center space-x-1 px-2 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-xs font-semibold whitespace-nowrap"
                                  title={t(
                                    "workflows:editDetails",
                                    "Detayları Düzenle",
                                  )}
                                >
                                  <PencilIcon className="w-3.5 h-3.5" />
                                  <span>{t("common:edit", "Düzenle")}</span>
                                </button>

                                <button
                                  onClick={() =>
                                    handleViewWorkflowDetails(
                                      projectWorkflows[project?.id],
                                    )
                                  }
                                  className="flex items-center space-x-1 px-2 py-1.5 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-xs font-semibold whitespace-nowrap"
                                  title={t("common:details")}
                                >
                                  <EyeIcon className="w-3.5 h-3.5" />
                                  <span>
                                    {t("common:detailsShort", "Detay")}
                                  </span>
                                </button>
                              </>
                            )}
                            {!projectWorkflows[project?.id] && (
                              <button
                                onClick={() => handleEditWorkflow(project?.id!)}
                                className="flex items-center space-x-1 px-2.5 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-xs font-semibold whitespace-nowrap"
                                title={editWorkflowText}
                              >
                                <Cog6ToothIcon className="w-3.5 h-3.5" />
                                <span>{t("workflows:design")}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Mobile & Tablet Layout */}
                      <div className="lg:hidden space-y-2.5">
                        {/* Header Row */}
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            {canCreateProject && (
                              <span
                                className="text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing"
                                title="Sürükleyerek sırala"
                              >
                                <Bars3Icon className="w-4 h-4" />
                              </span>
                            )}
                            <h3 className="text-xs font-bold text-slate-800 truncate">
                              {projectWorkflows[project?.id]?.name ||
                                getProjectFormValue(project, "name") ||
                                workflowNameText}
                            </h3>
                          </div>
                          <span
                            className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ml-2 flex-shrink-0 ${getStatusColor(
                              project?.status,
                            )}`}
                          >
                            {getStatusDisplayName(project?.status)}
                          </span>
                        </div>

                        {/* Info Grid */}
                        <div className="grid grid-cols-2 gap-2.5 text-xs">
                          <div>
                            <span className="text-slate-500 font-medium">
                              {t("workflows:nodes")}
                            </span>
                            <div className="flex items-center space-x-1">
                              <Squares2X2Icon className="w-4 h-4 text-blue-500" />
                              <p className="text-slate-800 font-semibold">
                                {projectWorkflows[project?.id]?.nodes?.length ||
                                  0}{" "}
                                {t("workflows:addNode")}
                              </p>
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-500 font-medium">
                              {t("workflows:edges")}
                            </span>
                            <div className="flex items-center space-x-1">
                              <ChartBarIcon className="w-4 h-4 text-purple-500" />
                              <p className="text-slate-800 font-semibold">
                                {projectWorkflows[project?.id]?.edges?.length ||
                                  0}{" "}
                                {t("workflows:connectNodes")}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap justify-end gap-1">
                          {projectWorkflows[project?.id] && (
                            <>
                              <button
                                onClick={() => handleAddMember(project)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-50 text-emerald-700"
                                title={t("projects:addMember")}
                              >
                                <UserPlusIcon className="w-4 h-4" />
                                <span className="sr-only">
                                  {t("projects:addMember")}
                                </span>
                              </button>

                              <button
                                onClick={() => handleEditWorkflow(project?.id!)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-700"
                                title={t("workflows:design")}
                              >
                                <Cog6ToothIcon className="w-4 h-4" />
                                <span className="sr-only">
                                  {t("workflows:design")}
                                </span>
                              </button>

                              <button
                                onClick={() =>
                                  handleStartWorkflow(project?.id!)
                                }
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-purple-50 text-purple-700"
                                title={t("projects:createProject")}
                              >
                                <RocketLaunchIcon className="w-4 h-4" />
                                <span className="sr-only">
                                  {t("projects:createProject")}
                                </span>
                              </button>

                              <button
                                onClick={() => handleEditDetails(project)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-amber-50 text-amber-700"
                                title={t("workflows:editDetails")}
                              >
                                <PencilIcon className="w-4 h-4" />
                                <span className="sr-only">
                                  {t("common:edit")}
                                </span>
                              </button>

                              <button
                                onClick={() =>
                                  handleViewWorkflowDetails(
                                    projectWorkflows[project?.id],
                                  )
                                }
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-indigo-50 text-indigo-700"
                                title={t("common:details")}
                              >
                                <EyeIcon className="w-4 h-4" />
                                <span className="sr-only">
                                  {t("common:detailsShort")}
                                </span>
                              </button>
                            </>
                          )}

                          {!projectWorkflows[project?.id] && (
                            <button
                              onClick={() => handleEditWorkflow(project?.id!)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-700"
                              title={t("workflows:design")}
                            >
                              <Cog6ToothIcon className="w-4 h-4" />
                              <span className="sr-only">
                                {t("workflows:design")}
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Hover Preview Popup - Desktop Only */}
                    {hoveredProjectId === project.id &&
                      projectWorkflows[project?.id] &&
                      projectWorkflows[project?.id].nodes?.length > 0 && (
                        <div
                          className={`hidden lg:block absolute left-1/2 ${isNearBottom ? "bottom-full mb-2 slide-in-from-bottom-2" : "top-full mt-2 slide-in-from-top-2"} transform -translate-x-1/2 z-[9999] animate-in fade-in duration-200`}
                        >
                          <div className="bg-white rounded-xl shadow-2xl border-2 border-blue-200 p-4 min-w-[400px]">
                            <div className="mb-2 flex items-center justify-between">
                              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                Workflow Preview
                              </span>
                              <span className="text-xs text-slate-500">
                                {projectWorkflows[project?.id].nodes?.length ||
                                  0}{" "}
                                Node
                              </span>
                            </div>
                            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-3">
                              <WorkflowMiniPreview
                                nodeTemplates={projectWorkflows[
                                  project?.id
                                ].nodes.map((node: any) => {
                                  const nodeType = (node.type || "ACTIVITY")
                                    .toString()
                                    .toUpperCase();
                                  const posX =
                                    node.positionX ?? node.position?.x ?? 0;
                                  const posY =
                                    node.positionY ?? node.position?.y ?? 0;
                                  const label =
                                    node.label ?? node.data?.label ?? nodeType;

                                  return {
                                    nodeId: node.id,
                                    nodeType: nodeType,
                                    nodeData: JSON.stringify({
                                      label: label,
                                      description:
                                        node.description ??
                                        node.data?.description,
                                      ...(node.data || {}),
                                    }),
                                    positionX: posX,
                                    positionY: posY,
                                  };
                                })}
                                edgeTemplates={(
                                  projectWorkflows[project?.id].edges || []
                                ).map((edge: any) => ({
                                  edgeId: edge.id,
                                  sourceNodeId:
                                    edge.source || edge.sourceNodeId,
                                  targetNodeId:
                                    edge.target || edge.targetNodeId,
                                  edgeData:
                                    typeof edge.edgeData === "string"
                                      ? edge.edgeData
                                      : JSON.stringify(edge.data || {}),
                                }))}
                                width={380}
                                height={200}
                                className="shadow-sm"
                              />
                            </div>
                          </div>
                          {/* Arrow pointer */}
                          <div
                            className={`absolute left-1/2 ${isNearBottom ? "-bottom-2" : "-top-2"} transform -translate-x-1/2`}
                          >
                            <div
                              className={`w-4 h-4 bg-white ${isNearBottom ? "border-b-2 border-r-2" : "border-t-2 border-l-2"} border-blue-200 transform rotate-45`}
                            ></div>
                          </div>
                        </div>
                      )}
                  </div>
                );
              })}
          </div>

          {/* Pagination Controls */}
          {filteredProjects.length > 0 && (
            <div className="px-5 py-3.5 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                {/* Results info */}
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{filteredProjects.length}</span>{" "}
                  {t("common:recordFound")}
                  {searchTerm && (
                    <span className="text-gray-400">
                      {" "}
                      ({t("common:total")} {projects.length}{" "}
                      {t("common:recordOf")})
                    </span>
                  )}
                </div>

                {/* Page controls */}
                <div className="flex items-center gap-2">
                  {/* Show page navigation only if multiple pages */}
                  {totalPages > 1 && (
                    <>
                      {/* Previous button */}
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeftIcon className="w-5 h-5" />
                      </button>

                      {/* Page numbers */}
                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter((page) => {
                            // Show first page, last page, current page, and adjacent pages
                            return (
                              page === 1 ||
                              page === totalPages ||
                              Math.abs(page - currentPage) <= 1
                            );
                          })
                          .map((page, idx, arr) => (
                            <React.Fragment key={page}>
                              {/* Add ellipsis */}
                              {idx > 0 && arr[idx - 1] !== page - 1 && (
                                <span className="px-2 text-gray-400 text-sm">
                                  ...
                                </span>
                              )}
                              <button
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1.5 text-sm rounded-lg ${
                                  currentPage === page
                                    ? "bg-blue-600 text-white font-medium"
                                    : "border border-gray-300 hover:bg-gray-100"
                                }`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          ))}
                      </div>

                      {/* Next button */}
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRightIcon className="w-5 h-5" />
                      </button>
                    </>
                  )}

                  {/* Items per page - always show */}
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className={`${
                      totalPages > 1 ? "ml-3" : ""
                    } px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value={10}>10 {t("common:itemsPerPage")}</option>
                    <option value={20}>20 {t("common:itemsPerPage")}</option>
                    <option value={50}>50 {t("common:itemsPerPage")}</option>
                    <option value={-1}>{t("common:showAll")}</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Empty State */}
        {(!projects || projects.length === 0) && !loading && (
          <div className="text-center py-16">
            <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-12 border border-white/20 shadow-xl max-w-lg mx-auto">
              <CubeIcon className="w-24 h-24 text-slate-300 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-slate-700 mb-4">
                {noWorkflowsCommonText}
              </h3>
              <p className="text-slate-500 mb-8 leading-relaxed">
                {createFirstWorkflowText}
              </p>
              {!canCreateProject && (
                <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-4">
                  <p className="text-amber-700 text-sm">
                    {t("common:managerPermissionRequired")}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Workflow Start Modal */}
      {showWorkflowStartModal &&
        selectedWorkflowId &&
        selectedProjectIdForWorkflow && (
          <WorkflowStartModal
            isOpen={showWorkflowStartModal}
            onClose={() => setShowWorkflowStartModal(false)}
            projectId={selectedProjectIdForWorkflow}
            workflowId={selectedWorkflowId}
            onSuccess={handleWorkflowStartSuccess}
          />
        )}

      {/* Edit Details Modal */}
      {showEditDetailsModal && selectedProjectForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[95vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <PencilIcon className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {t("workflows:editDetails")}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("workflows:editDetailsDescription")}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowEditDetailsModal(false);
                    setSelectedProjectForDetails(null);
                    setSelectedWorkflowForDetails(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Form Builder Content */}
            <div className="flex-1 overflow-hidden">
              <ProjectFormBuilder
                key={`edit-details-${selectedProjectForDetails.id}-${editFormElements.length}`}
                initialData={{
                  workflowName:
                    selectedWorkflowForDetails?.name ||
                    getProjectFormValue(selectedProjectForDetails, "name") ||
                    workflowNameText,
                  formName:
                    selectedWorkflowForDetails?.name ||
                    getProjectFormValue(selectedProjectForDetails, "name") ||
                    workflowNameText,
                  elements:
                    editFormElements.length > 0
                      ? editFormElements
                      : selectedProjectForDetails.formElements || [],
                  elementOrder: JSON.stringify(
                    (editFormElements.length > 0
                      ? editFormElements
                      : selectedProjectForDetails.formElements || []
                    ).map((el) => el.id),
                  ),
                  projectManagerIds: editProjectManagerIds,
                }}
                isEditMode={true}
                onSave={handleSaveDetails}
                onCancel={() => {
                  setShowEditDetailsModal(false);
                  setSelectedProjectForDetails(null);
                  setSelectedWorkflowForDetails(null);
                }}
                workflowContainerId={selectedProjectForDetails.id}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
